
export default function UXPrinciples() {
  const principles = [
    {
      number: '01',
      title: 'Progressive Disclosure',
      description: 'Show only what\'s needed at each step. Complex features are revealed contextually to reduce cognitive overload.',
      icon: 'ri-stack-line',
      color: '#1FA372',
      bgColor: '#F0FDF9'
    },
    {
      number: '02',
      title: 'Role-Based Simplicity',
      description: 'Each user type sees a tailored interface. Admins get full control, residents get streamlined self-service.',
      icon: 'ri-user-settings-line',
      color: '#F97316',
      bgColor: '#FFF7ED'
    },
    {
      number: '03',
      title: 'Transparency by Default',
      description: 'Every action, payment, and decision is tracked and visible. Build trust through radical openness.',
      icon: 'ri-eye-line',
      color: '#3B82F6',
      bgColor: '#EFF6FF'
    },
    {
      number: '04',
      title: 'Mobile-First Architecture',
      description: 'Designed for thumbs first, then scaled up. 70% of users access HOA apps on mobile devices.',
      icon: 'ri-smartphone-line',
      color: '#8B5CF6',
      bgColor: '#F5F3FF'
    }
  ];

  const hypotheses = [
    {
      hypothesis: 'If we reduce payment steps from 7 to 3, then payment completion rates will increase by 40%.',
      status: 'Validated',
      result: '+52% completion'
    },
    {
      hypothesis: 'If we add real-time status updates, then support ticket volume will decrease by 30%.',
      status: 'Validated',
      result: '-45% tickets'
    },
    {
      hypothesis: 'If we implement smart notifications, then user engagement will increase by 25%.',
      status: 'Validated',
      result: '+38% engagement'
    }
  ];

  return (
    <section className="py-24 bg-[#FAFBFC]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#1FA372]/10 text-[#1FA372] rounded-full text-sm font-semibold mb-4 border border-[#1FA372]/20">
            05 — Strategy
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-6">
            UX Principles & Design Hypotheses
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto">
            The foundational principles guiding every design decision and the hypotheses we tested to validate our approach.
          </p>
        </div>

        {/* Principles Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {principles.map((principle, index) => (
            <div 
              key={index}
              className="bg-white rounded-3xl p-8 border border-[#E2E8F0] hover:shadow-lg transition-all relative overflow-hidden group"
            >
              <div 
                className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"
                style={{ backgroundColor: principle.color }}
              ></div>
              
              <div className="relative flex gap-6">
                <div className="flex-shrink-0">
                  <span 
                    className="text-6xl font-bold opacity-20"
                    style={{ color: principle.color }}
                  >
                    {principle.number}
                  </span>
                </div>
                <div>
                  <div 
                    className="w-12 h-12 flex items-center justify-center rounded-xl mb-4 border"
                    style={{ backgroundColor: principle.bgColor, borderColor: `${principle.color}30` }}
                  >
                    <i className={`${principle.icon} text-xl`} style={{ color: principle.color }}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E293B] mb-3">{principle.title}</h3>
                  <p className="text-[#64748B] leading-relaxed">{principle.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Design Hypotheses */}
        <div className="bg-gradient-to-br from-[#F0FDF9] to-white rounded-3xl p-8 border border-[#1FA372]/20 shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-8 flex items-center justify-center gap-3">
            <i className="ri-flask-line text-[#1FA372]"></i>
            Design Hypotheses & Validation
          </h3>

          <div className="space-y-4">
            {hypotheses.map((item, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-[#E2E8F0] shadow-sm"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-[#1E293B] leading-relaxed">{item.hypothesis}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="px-4 py-2 bg-[#1FA372]/10 text-[#1FA372] rounded-full text-sm font-semibold border border-[#1FA372]/20 whitespace-nowrap">
                      {item.status}
                    </span>
                    <span className="text-[#1FA372] font-bold text-lg whitespace-nowrap">
                      {item.result}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Design Process */}
        <div className="mt-16 grid md:grid-cols-5 gap-4">
          {['Discover', 'Define', 'Ideate', 'Prototype', 'Test'].map((step, index) => (
            <div key={index} className="relative">
              <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0] text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-[#1FA372]/10 rounded-full mx-auto mb-4 border border-[#1FA372]/20">
                  <span className="text-[#1FA372] font-bold">{index + 1}</span>
                </div>
                <h4 className="text-[#1E293B] font-semibold">{step}</h4>
              </div>
              {index < 4 && (
                <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                  <i className="ri-arrow-right-s-line text-[#1FA372] text-2xl"></i>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
